// Hideo - sync state of every profile on this device (the floppy-disk file, the data folder, the online save):
// dirty = changes not yet saved to a file or online; file = last saved/loaded file { savedAt (ISO, date written in the
// file), savedOn ('telefono'|'computer'), kind ('salvato'|'caricato'|'cartella'|'online-salvato'|'online-caricato'; old
// ones 'drive-…'), provider (online service) }. All of these are stored values: show the device with tv().
// The state is SYNC in src/stores/sync.ts (localStorage creatorOS_sync). Ported from the legacy core/data.js (verbatim
// copy in tests/fixtures/legacy/data.js, golden tests in tests/saving*.test.ts).
import { LOCALE, t } from '../../i18n'
import { useDataStore } from '../../stores/data'
import { useSyncStore } from '../../stores/sync'
import { useUiStore } from '../../stores/ui'
import type { SyncState } from '../cloud'
import { renderSyncUi } from '../cloud/autosync'
import { FOLDER } from './folder'
import { SYNC_KEY } from '../../data/profiles'

/** 'telefono' on Android and iOS, otherwise 'computer' (the stored savedOn of profile files) */
export const DEVICE = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'telefono' : 'computer'
/** A profile's save state ({} when it has none) */
export const syncOf = (id: string): SyncState => useSyncStore().syncOf(id)
/** Year, month, day, hour and minute of a moment in local time, two digits each (the year four): the names of the files
 *  saved by Hideo (never translated, they are stored names) */
export function timeParts(n = new Date()): { y: string; mo: string; d: string; h: string; mi: string } {
  const p = (x: number) => String(x).padStart(2, '0')
  return { y: String(n.getFullYear()), mo: p(n.getMonth() + 1), d: p(n.getDate()), h: p(n.getHours()), mi: p(n.getMinutes()) }
}
/** Date and device written with a file */
export const fileStamp = () => ({ savedAt: new Date().toISOString(), savedOn: DEVICE })
/** Date and time of a save, in the UI language ('' if not valid) */
export const fmtWhen = (iso?: string | null): string => { const when = new Date(iso as string); return isNaN(when as unknown as number) ? '' : when.toLocaleString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }
/** Changes the save state of a profile (merged into the old one) and redraws what shows it (sync.ver: the Profiles
 *  window, the service choice and the Home card; not every page, which ui.refresh would redraw at every online save) */
export function setSync(id: string, info: SyncState): void {
  const sync = useSyncStore(), SYNC = sync.SYNC
  SYNC[id] = { ...syncOf(id), ...info }
  try { localStorage.setItem(SYNC_KEY, JSON.stringify(SYNC)) } catch { /* storage full or blocked */ }
  refreshWarn(); sync.ver++; renderSyncUi()
}
/** Dot on the profile button (#folderWarn, TopControls.vue): Drive autosave paused, or changes of the active profile not
 *  yet saved. (The "data folder" has no buttons any more: if the browser grants permission it keeps working silently,
 *  otherwise no warning) */
export function refreshWarn(): void {
  const dirty = !FOLDER.ok && syncOf(useDataStore().profiles.current).dirty
  const paused = useSyncStore().paused
  useUiStore().profileWarn = paused ? paused : dirty ? t('Changes not saved yet (Save online in the Profiles window)') : ''
}
